import { type ReactElement, type ReactNode } from "react";

import { cn } from "../utils/cn";

export interface SwitchProps {
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  /** Accessible name; the visible label (if any) must be contained in it. */
  "aria-label": string;
  /** Visible content before the track, e.g. an icon and a short label. */
  children?: ReactNode;
  disabled?: boolean;
  className?: string;
}

/** An on/off switch: a track with a thumb, `role="switch"` + `aria-checked`.
 * Not a pill button: a pill doesn't show its state. */
export function Switch({
  checked,
  onCheckedChange,
  children,
  disabled,
  className,
  ...aria
}: SwitchProps): ReactElement {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={aria["aria-label"]}
      disabled={disabled}
      onClick={() => onCheckedChange(!checked)}
      className={cn(
        "inline-flex cursor-pointer items-center gap-2 rounded-md text-sm font-medium",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2",
        "disabled:cursor-default disabled:opacity-50",
        className,
      )}
    >
      {children}
      <span
        aria-hidden="true"
        className={cn(
          "relative inline-flex h-4 w-7 shrink-0 rounded-full transition-colors motion-safe:duration-(--duration-fast)",
          // Off: dark enough to read as a control on white (WCAG 1.4.11, 3:1).
          checked ? "bg-hide-mode-fg/30" : "bg-neutral-500",
        )}
      >
        <span
          className={cn(
            "absolute top-0.5 h-3 w-3 rounded-full bg-neutral-0 shadow-sm motion-safe:transition-transform motion-safe:duration-(--duration-fast)",
            checked ? "translate-x-3.5" : "translate-x-0.5",
          )}
        />
      </span>
    </button>
  );
}
